/**
 * ClassificacaoDocumentoSection Component - Classificação Documento management section
 */
import React from 'react';
import { Plus, Pencil, Trash2, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import PermissionGate from '../PermissionGate';

interface ClassificacaoDocumento {
  id: number;
  classificacao: string;
  abreviacao: string;
}

interface NewClassificacao {
  classificacao: string;
  abreviacao: string;
}

interface ClassificacaoDocumentoSectionProps {
  classificacoes: ClassificacaoDocumento[];
  isLoadingClassificacoes: boolean;
  isSavingClassificacao: boolean;
  editingClassificacao: number | null;
  newClassificacao: NewClassificacao;
  editClassificacaoData: NewClassificacao;
  setNewClassificacao: (classificacao: NewClassificacao) => void;
  setEditClassificacaoData: (classificacao: NewClassificacao) => void;
  onCreateClassificacao: () => Promise<void>;
  onUpdateClassificacao: (id: number) => Promise<void>;
  onDeleteClassificacao: (id: number) => Promise<void>;
  onStartEditing: (classificacao: ClassificacaoDocumento) => void;
  onCancelEditing: () => void;
}

export const ClassificacaoDocumentoSection: React.FC<ClassificacaoDocumentoSectionProps> = ({
  classificacoes,
  isLoadingClassificacoes,
  isSavingClassificacao,
  editingClassificacao,
  newClassificacao,
  editClassificacaoData,
  setNewClassificacao,
  setEditClassificacaoData,
  onCreateClassificacao,
  onUpdateClassificacao,
  onDeleteClassificacao,
  onStartEditing,
  onCancelEditing,
}) => {
  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Classificação de Documento</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Gerencie as classificações de documentos disponíveis no sistema (evidências e relatórios).
        </p>

        {/* Form de Adição */}
        <div className="flex gap-4 mb-8 items-end bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex-1">
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1 uppercase">Classificação</label>
            <input
              type="text"
              value={newClassificacao.classificacao}
              onChange={(e) => setNewClassificacao({ ...newClassificacao, classificacao: e.target.value })}
              placeholder="Ex: Evidência Sobra"
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1 uppercase">Abreviação Arquivo</label>
            <input
              type="text"
              value={newClassificacao.abreviacao}
              onChange={(e) => setNewClassificacao({ ...newClassificacao, abreviacao: e.target.value })}
              placeholder="Ex: Ev_Sobra"
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          <PermissionGate tela="CONFIGURACOES_VIEW" acao="criar">
            <button
              onClick={onCreateClassificacao}
              disabled={isSavingClassificacao}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Plus size={18} />
              Adicionar
            </button>
          </PermissionGate>
        </div>

        {/* Tabela */}
        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400 font-medium border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-4 py-3">Classificação</th>
                <th className="px-4 py-3">Abreviação Arquivo</th>
                <th className="px-4 py-3 w-24 text-center">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {isLoadingClassificacoes ? (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                    <Loader2 className="animate-spin mx-auto mb-2" />
                    Carregando...
                  </td>
                </tr>
              ) : classificacoes.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                    Nenhum registro encontrado.
                  </td>
                </tr>
              ) : (
                classificacoes.map((item) => (
                  <tr key={item.id} className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    {editingClassificacao === item.id ? (
                      <>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={editClassificacaoData.classificacao}
                            onChange={(e) => setEditClassificacaoData({ ...editClassificacaoData, classificacao: e.target.value })}
                            className="w-full px-2 py-1 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={editClassificacaoData.abreviacao}
                            onChange={(e) => setEditClassificacaoData({ ...editClassificacaoData, abreviacao: e.target.value })}
                            className="w-full px-2 py-1 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          />
                        </td>
                        <td className="px-4 py-3 flex justify-center gap-2">
                          <PermissionGate tela="CONFIGURACOES_VIEW" acao="editar">
                            <button onClick={() => onUpdateClassificacao(item.id)} className="text-green-600 hover:text-green-700" title="Salvar">
                              <CheckCircle size={18} />
                            </button>
                          </PermissionGate>
                          <button onClick={onCancelEditing} className="text-red-500 hover:text-red-600" title="Cancelar">
                            <XCircle size={18} />
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{item.classificacao}</td>
                        <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{item.abreviacao}</td>
                        <td className="px-4 py-3 flex justify-center gap-2">
                          <PermissionGate tela="CONFIGURACOES_VIEW" acao="editar">
                            <button onClick={() => onStartEditing(item)} className="text-blue-600 hover:text-blue-700" title="Editar">
                              <Pencil size={18} />
                            </button>
                          </PermissionGate>
                          <PermissionGate tela="CONFIGURACOES_VIEW" acao="excluir">
                            <button onClick={() => onDeleteClassificacao(item.id)} className="text-red-500 hover:text-red-600" title="Excluir">
                              <Trash2 size={18} />
                            </button>
                          </PermissionGate>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ClassificacaoDocumentoSection;
